import type { Conversation } from "@rulebase/core/api";
import { ConversationTimeDisplay } from "@rulebase/core/components";
import { Fragment } from "react";
import { RiArrowDownSLine } from "react-icons/ri";
import { Markdown } from "./ui/markdown";

/**
 * The open ticket: its subject as the title, the generated summary, then a
 * quiet params line. The links toggle lives at the right of that line rather
 * than in a header of its own, which saves a row in a panel that is short on
 * vertical space.
 */
export function TicketHeader(props: {
  conversation: Conversation;
  ticketNumber: string | null | undefined;
  customerName: string | null | undefined;
  companyName: string | null | undefined;
  canToggleLinks: boolean;
  linksOpen: boolean;
  onToggleLinks: () => void;
}) {
  const { conversation } = props;

  const params: { key: string; node: React.ReactNode }[] = [];
  if (props.customerName) {
    params.push({
      key: "customer",
      node: <span className="truncate">{props.customerName}</span>,
    });
  }
  if (props.companyName) {
    params.push({
      key: "company",
      node: <span className="truncate">{props.companyName}</span>,
    });
  }
  if (conversation.started_at) {
    params.push({
      key: "started",
      node: (
        <span className="flex items-baseline gap-1 whitespace-nowrap">
          Started
          <ConversationTimeDisplay
            date={conversation.started_at}
            variant="long"
          />
        </span>
      ),
    });
  }

  return (
    <div className="flex w-full flex-col gap-2.5">
      <div className="flex flex-wrap items-baseline gap-2">
        <h1 className="text-[15px] leading-5 font-medium text-neutral-700">
          {conversation.subject}
        </h1>
        {props.ticketNumber && (
          <span className="text-neutral-500">(#{props.ticketNumber})</span>
        )}
      </div>

      {conversation.description && (
        <Markdown className="prose prose-neutral w-full max-w-full text-base leading-relaxed text-neutral-700 [&_h2]:scroll-mt-4 [&_h2]:text-base [&_h3]:scroll-mt-4 [&_h3]:text-base">
          {conversation.description}
        </Markdown>
      )}

      <div className="flex w-full items-baseline gap-2 text-[13.5px] text-neutral-500">
        {/* Composed from a list so a missing value never leaves a leading dot. */}
        {params.map((param, index) => (
          <Fragment key={param.key}>
            {index > 0 && <Dot />}
            {param.node}
          </Fragment>
        ))}

        {props.canToggleLinks && (
          <button
            type="button"
            onClick={props.onToggleLinks}
            aria-expanded={props.linksOpen}
            className="ml-auto flex shrink-0 cursor-pointer items-center gap-1 self-center text-neutral-500 transition-colors hover:text-neutral-600"
          >
            <RiArrowDownSLine
              className={
                props.linksOpen
                  ? "size-3.5 rotate-180 transition-transform"
                  : "size-3.5 transition-transform"
              }
            />
            {props.linksOpen ? "Hide links" : "Show links"}
          </button>
        )}
      </div>
    </div>
  );
}

function Dot() {
  return (
    <span className="size-0.5 shrink-0 self-center rounded-full bg-neutral-400" />
  );
}
